import { lazy } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import ProtectedRoute from '@/components/ProtectedRoute';
import { HORA_EXTRA_PERMISSOES } from '@/lib/rhHoraExtra';

const RHHub = lazy(() => import('./Hub'));
const RHPainel = lazy(() => import('./Painel/Index'));
const RHSolicitacaoForm = lazy(() => import('./Solicitacoes/Form'));
const RHSolicitacaoDetail = lazy(() => import('./Solicitacoes/Detail'));
const RHAdmin = lazy(() => import('./Admin'));
const RHCargoAprovadores = lazy(() => import('./CargoAprovadores/Index'));
const RHLocaisTrabalho = lazy(() => import('./LocaisTrabalho/Index'));
const RHTalentos = lazy(() => import('./Talentos/Index'));
const RHMovimentacoes = lazy(() => import('./Movimentacoes/Index'));
const RHMovimentacaoForm = lazy(() => import('./Movimentacoes/Form'));
const RHMovimentacoesHistorico = lazy(() => import('./Movimentacoes/Historico'));
const RHMovimentacoesSolicitacoes = lazy(() => import('./Movimentacoes/Solicitacoes'));
const RHHoraExtra = lazy(() => import('./HoraExtra/Index'));
const RHDesligamentoIndex = lazy(() => import('./Desligamento/Index'));
const RHDesligamentoForm = lazy(() => import('./Desligamento/Form'));
const RHDesligamentoDetail = lazy(() => import('./Desligamento/Detail'));
const RHDesligamentoResponder = lazy(() => import('./Desligamento/Responder'));

export const rhRoutes: RouteObject[] = [
    {
        path: '/rh/desligamento',
        element: (
            <ProtectedRoute permission="rh.desligamento.manage">
                <RHDesligamentoIndex />
            </ProtectedRoute>
        ),
    },
    {
        path: '/rh/desligamento/nova',
        element: (
            <ProtectedRoute permission="rh.desligamento.manage">
                <RHDesligamentoForm />
            </ProtectedRoute>
        ),
    },
    {
        path: '/rh/desligamento/:id',
        element: (
            <ProtectedRoute permission="rh.desligamento.manage">
                <RHDesligamentoDetail />
            </ProtectedRoute>
        ),
    },
    {
        // Questionário preenchido pelo RH durante a entrevista (rota oculta, botão Voltar).
        path: '/rh/desligamento/:id/responder',
        element: (
            <ProtectedRoute permission="rh.desligamento.manage">
                <RHDesligamentoResponder />
            </ProtectedRoute>
        ),
    },
    {
        // Hub do módulo (mini launcher). Sem permission: os cards filtram por permissão.
        path: '/rh',
        element: (
            <ProtectedRoute>
                <RHHub />
            </ProtectedRoute>
        ),
    },
    {
        path: '/rh/movimentacoes',
        element: (
            <ProtectedRoute anyOf={['rh.movimentacao', 'rh.movimentacao.manage']}>
                <RHMovimentacoes />
            </ProtectedRoute>
        ),
    },
    {
        path: '/rh/movimentacoes/nova/:tipo',
        element: (
            <ProtectedRoute anyOf={['rh.movimentacao', 'rh.movimentacao.manage']}>
                <RHMovimentacaoForm />
            </ProtectedRoute>
        ),
    },
    {
        path: '/rh/movimentacoes/historico',
        element: (
            <ProtectedRoute anyOf={['rh.movimentacao', 'rh.movimentacao.manage']}>
                <RHMovimentacoesHistorico />
            </ProtectedRoute>
        ),
    },
    {
        // Só auth, como /rh/solicitacoes: aprovador de esteira pode não ter
        // nenhuma rh.*; o /painel devolve apenas o que é dele.
        path: '/rh/movimentacoes/solicitacoes',
        element: (
            <ProtectedRoute>
                <RHMovimentacoesSolicitacoes />
            </ProtectedRoute>
        ),
    },
    {
        // As permissões são independentes no Spatie: quem só administra os
        // parâmetros não tem `.aprovar`, e precisa entrar mesmo assim.
        path: '/rh/hora-extra',
        element: (
            <ProtectedRoute anyOf={[...HORA_EXTRA_PERMISSOES]}>
                <RHHoraExtra />
            </ProtectedRoute>
        ),
    },
    {
        path: '/rh/locais-trabalho',
        element: (
            <ProtectedRoute permission="rh.aprovacao.manage">
                <RHLocaisTrabalho />
            </ProtectedRoute>
        ),
    },
    {
        path: '/rh/cargo-aprovadores',
        element: (
            <ProtectedRoute permission="rh.aprovacao.manage">
                <RHCargoAprovadores />
            </ProtectedRoute>
        ),
    },
    {
        path: '/rh/admin',
        element: (
            <ProtectedRoute permission="rh.aprovacao.manage">
                <RHAdmin />
            </ProtectedRoute>
        ),
    },
    {
        // Quadro de vagas legado removido (2026-10-09): o fluxo é Solicitações.
        path: '/rh/vagas',
        element: <Navigate to="/rh/solicitacoes" replace />,
    },
    {
        // Painel antigo — redireciona pro hub único (bookmarks + notificações antigas).
        path: '/rh/aprovacoes',
        element: <Navigate to="/rh/solicitacoes" replace />,
    },
    {
        path: '/rh/talentos',
        element: (
            <ProtectedRoute permission="rh.talentos.manage">
                <RHTalentos />
            </ProtectedRoute>
        ),
    },
    {
        // Sem permission: hub adaptativo — aprovador de esteira pode não ter
        // nenhuma rh.*; o backend (/painel auto-escopado) decide o conteúdo.
        path: '/rh/solicitacoes',
        element: (
            <ProtectedRoute>
                <RHPainel />
            </ProtectedRoute>
        ),
    },
    {
        path: '/rh/solicitacoes/nova',
        element: (
            <ProtectedRoute permission="rh.sol.vagas">
                <RHSolicitacaoForm />
            </ProtectedRoute>
        ),
    },
    {
        // Sem permission: o backend (SolicitacaoVagaPolicy::view) decide — aprovador
        // da etapa corrente pode abrir o detalhe mesmo sem rh.sol.vagas.
        path: '/rh/solicitacoes/:id',
        element: (
            <ProtectedRoute>
                <RHSolicitacaoDetail />
            </ProtectedRoute>
        ),
    },
];
